/**
 * Duplicate a requirement, end to end (Akhil, 2026-09-16). Pure of hooks — the API is injected,
 * so the sequence is testable — and wrapped by `useDuplicateRequirement`.
 *
 * The steps, in order, and what a failure at each means:
 *
 *   1. read the source enquiry and its quotation      nothing made yet; a plain error
 *   2. `create_enquiry` with every line               the copy EXISTS from here on
 *   3. route + contract rate onto the new rows        best effort — a warning
 *   4. `create_quotation` from the source's lines      an error naming the new enquiry, so the
 *                                                     purchaser can find (and finish or delete) it
 *   5. the document terms — charges, discount, taxes   best effort — a warning
 *   6. the copy to `Comparison Done`                   best effort — a warning; it is what makes
 *                                                     the Requirements card open the quotation
 *
 * See `lib/duplicateRequirement.ts` for what is copied and why rows are matched by position.
 */

import { createEnquiryFlow } from './createEnquiryFlow'
import { toEnquiry, type WireEnquiryDetail } from './useEnquiries'
import { buildUpdateEnquiryWireBody, type UpdateEnquiryPatch } from './useEnquiryMutations'
import type { Enquiry, EnquiryItemPatch } from '../types'
import { termsToWire } from './useQuotation'
import { getBaseCurrency } from './baseCurrency'
import { completeQuotationPayload } from '../quotationPayload'
import {
  duplicateEnquiryHeader,
  duplicateQuotationPayload,
  duplicateQuotationTerms,
  pricingPatchesByPosition,
  type WireQuotationDoc,
} from '../duplicateRequirement'

export interface DuplicateApi {
  callGet: <T>(module: string, method: string, params?: Record<string, unknown>) => Promise<T>
  callPost: <T>(module: string, method: string, body?: unknown) => Promise<T>
  callPut: <T>(module: string, method: string, body?: unknown) => Promise<T>
  /** The source's current quotation, or `null` when it has none. */
  findQuotationName: (enquiryId: string) => Promise<string | null>
  /** Many line patches in one wholesale `update_enquiry` — `useEnquiryMutations`' primitive. */
  updateLines: (enquiryId: string, patches: Array<{ lineId: string; patch: EnquiryItemPatch }>) => Promise<void>
}

export interface DuplicateResult {
  enquiryId: string
  quotationId: string
  warnings: string[]
}

const reason = (error: unknown) => (error instanceof Error ? error.message : String(error))

/** The source header fields the create body cannot carry — every one that is set. */
export function copiedHeaderFields(h: Enquiry['header']): Partial<Pick<Enquiry['header'], 'customer_reference_no' | 'customer_reference_date' | 'sales_person' | 'department' | 'customer_currency' | 'price_list'>> & UpdateEnquiryPatch {
  return {
    ...(h.customer_reference_no ? { customer_reference_no: h.customer_reference_no } : {}),
    ...(h.customer_reference_date ? { customer_reference_date: h.customer_reference_date } : {}),
    ...(h.sales_person ? { sales_person: h.sales_person } : {}),
    ...(h.department ? { department: h.department } : {}),
    ...(h.customer_currency ? { customer_currency: h.customer_currency } : {}),
    ...(h.price_list ? { price_list: h.price_list } : {}),
  }
}

export async function duplicateRequirementFlow(sourceId: string, today: string, api: DuplicateApi): Promise<DuplicateResult> {
  const source = toEnquiry(await api.callGet<WireEnquiryDetail>('enquiry', 'get_enquiry', { name: sourceId }))
  const quotationName = await api.findQuotationName(sourceId)
  if (quotationName === null) throw new Error(`${sourceId} has no quotation to copy — duplicate a requirement that has been priced.`)
  const sourceQuotation = await api.callGet<WireQuotationDoc>('quotation', 'get_quotation', { name: quotationName })

  const created = await createEnquiryFlow(duplicateEnquiryHeader(source), today, api)
  const enquiryId = created.enquiry.id
  const warnings: string[] = created.warning ? [created.warning] : []

  /* ================= past this line the copy EXISTS — every failure names it ================= */

  // `createEnquiryFlow` reads the copy back; when that read failed it fell back to the sent body,
  // which carries no rows — and the rows' own serials are needed here, so it is read once more.
  let fresh = created.enquiry
  if (fresh.items.length === 0 && source.items.length > 0) {
    try {
      fresh = toEnquiry(await api.callGet<WireEnquiryDetail>('enquiry', 'get_enquiry', { name: enquiryId }))
    } catch (error) {
      warnings.push(`Created ${enquiryId}, but could not read it back (${reason(error)}).`)
    }
  }

  /*
    An EXACT copy (Akhil, 2026-09-18: "duplicate means an exact copy, then the user can update").
    `create_enquiry` has no body for the customer reference and date, the sales person, the
    department, the customer currency or the price list, so they are written straight after —
    and BEFORE the quotation, which is built from this header and which the server refuses
    without a reference and a sales person ("Value missing for Quotation: Customer Ref No" on
    ENQ-2026-00222).
  */
  const copied = copiedHeaderFields(source.header)
  const headerPatch = buildUpdateEnquiryWireBody(copied)
  if (Object.keys(headerPatch).length > 0) {
    try {
      await api.callPut('enquiry', 'update_enquiry', { name: enquiryId, data: headerPatch })
      fresh = { ...fresh, header: { ...fresh.header, ...copied } }
    } catch (error) {
      warnings.push(`Created ${enquiryId}, but its reference, sales person, department and currency could not be copied (${reason(error)}).`)
    }
  }

  const pricing = pricingPatchesByPosition(source.items, fresh.items)
  if (pricing.length > 0) {
    try {
      await api.updateLines(enquiryId, pricing)
    } catch (error) {
      warnings.push(`Created ${enquiryId}, but its contract-priced lines could not be marked (${reason(error)}).`)
    }
  }

  let quotationId: string
  try {
    const w = await api.callPost<{ name?: string }>('quotation', 'create_quotation', {
      // Completed like every other creator's body — the copy carries the same PO-side header.
      data: completeQuotationPayload(duplicateQuotationPayload(sourceQuotation, fresh, today), {
        baseCurrency: (typeof sourceQuotation.currency === 'string' && sourceQuotation.currency) || getBaseCurrency(),
        company: fresh.header.company,
        customerReferenceNo: fresh.header.customer_reference_no,
        enquiryType: fresh.header.enquiry_type,
      }),
    })
    quotationId = typeof w?.name === 'string' ? w.name.trim() : ''
    if (quotationId === '') throw new Error('quotation.create_quotation returned no quotation name')
  } catch (error) {
    throw new Error(`Created ${enquiryId} with its lines, but its quotation could not be created — ${reason(error)}. Open ${enquiryId} to price it, or delete it.`)
  }

  try {
    const terms = termsToWire(duplicateQuotationTerms(sourceQuotation))
    if (Object.keys(terms).length > 0) {
      await api.callPut('quotation', 'update_quotation', { name: quotationId, data: terms })
    }
  } catch (error) {
    warnings.push(`${quotationId} was created and priced, but its charges, discount and taxes could not be copied (${reason(error)}). Set them on the quotation.`)
  }

  try {
    await api.callPut('enquiry', 'update_enquiry', { name: enquiryId, data: buildUpdateEnquiryWireBody({ status: 'Comparison Done' }) })
  } catch (error) {
    warnings.push(`${enquiryId} was left at its opening status (${reason(error)}); its quotation is ${quotationId}.`)
  }

  return { enquiryId, quotationId, warnings }
}
